import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ledgerApi } from "../lib/ledgerApi";
import HashString from "../components/HashString";
import { ArrowLeft, ArrowDownLeft, ArrowUpRight } from "lucide-react";

export default function WalletDetail() {
  const { address } = useParams();
  const [wallet, setWallet] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setWallet(null);
    setError(null);
    const load = () =>
      ledgerApi
        .getWallet(address)
        .then((w) => alive && setWallet(w))
        .catch((e) => alive && setError(e?.response?.data?.detail || e.message));
    load();
    const i = setInterval(load, 3500);
    return () => {
      alive = false;
      clearInterval(i);
    };
  }, [address]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-10 md:py-14">
      <Link to="/wallets" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6" data-testid="back-wallets">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> All wallets
      </Link>
      <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">/ Wallet</div>
      <h1 className="text-2xl sm:text-3xl tracking-tight font-medium mb-2 break-all font-mono">{address}</h1>

      {error && <div className="border border-status-tampered/40 bg-status-tampered/10 p-4 rounded-sm text-status-tampered text-sm">{error}</div>}
      {!wallet && !error && <div className="text-sm text-muted-foreground">Loading…</div>}

      {wallet && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-6">
            <div className="md:col-span-1 p-5 border border-border rounded-sm bg-foreground text-background">
              <div className="text-[10px] uppercase tracking-[0.2em] opacity-60">Balances</div>
              <div className="space-y-1 mt-2" data-testid="wallet-detail-balance">
                {Object.keys(wallet.balances || {}).length === 0 && (
                  <div className="font-mono text-xl">{wallet.balance} NEO</div>
                )}
                {Object.entries(wallet.balances || {}).map(([ccy, amt]) => (
                  <div key={ccy} className="font-mono text-lg flex items-baseline justify-between gap-3">
                    <span>{amt}</span>
                    <span className="text-[10px] uppercase tracking-widest opacity-60">{ccy}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-2 p-5 border border-border rounded-sm bg-card font-mono text-xs space-y-2">
              <div><span className="text-muted-foreground uppercase tracking-widest text-[10px] mr-2">Label</span>{wallet.label || "—"}</div>
              <div className="break-all"><span className="text-muted-foreground uppercase tracking-widest text-[10px] mr-2">Pubkey</span>{wallet.public_key}</div>
              <div><span className="text-muted-foreground uppercase tracking-widest text-[10px] mr-2">Created</span>{wallet.created_at ? new Date(wallet.created_at).toLocaleString() : "—"}</div>
            </div>
          </div>

          <h2 className="text-lg font-medium mt-10 mb-3">Transaction history</h2>
          <div className="border border-border rounded-sm bg-card divide-y divide-border">
            {wallet.transactions.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground">No transactions.</div>
            )}
            {wallet.transactions.map((tx) => {
              const outgoing = tx.from_address === address;
              return (
                <Link
                  to={`/tx/${tx.tx_id}`}
                  key={tx.tx_id}
                  data-testid={`wallet-tx-${tx.tx_id}`}
                  className="grid grid-cols-12 gap-3 p-4 items-center hover:bg-muted/40 transition-colors"
                >
                  <div className="col-span-1 hidden sm:flex items-center justify-center">
                    {outgoing ? (
                      <ArrowUpRight className="w-4 h-4 text-status-tampered" strokeWidth={1.5} />
                    ) : (
                      <ArrowDownLeft className="w-4 h-4 text-status-ok" strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="col-span-12 sm:col-span-5 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                      {outgoing ? "Sent" : "Received"} · {tx.status}
                    </div>
                    <HashString value={tx.tx_id} label="Tx id" />
                  </div>
                  <div className="col-span-7 sm:col-span-4 min-w-0 text-xs font-mono text-muted-foreground">
                    <div className="truncate">
                      {outgoing ? "to " : "from "}
                      <span className="text-foreground">
                        {outgoing ? tx.to_address : tx.from_address}
                      </span>
                    </div>
                    <div className="text-[10px]">{new Date(tx.timestamp).toLocaleString()}</div>
                  </div>
                  <div className="col-span-5 sm:col-span-2 text-right font-mono">
                    <span className={outgoing ? "text-status-tampered" : "text-status-ok"}>
                      {outgoing ? "−" : "+"}{tx.amount}
                    </span>{" "}
                    <span className="text-muted-foreground">NEO</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
